import { normalizeRuntimeApiBaseUrl } from "./runtimeApiBase";
import { isInlineProgramConsentQuestion } from "./consentQuestionClassification";

export const TREATMENT_RUNTIME_DRAFT_SCHEMA_VERSION = 1;

const text = (value) => String(value ?? "");

export const treatmentRuntimeDraftKey = (baseKey, sessionToken) => (
  `${baseKey}:session:${text(sessionToken)}`
);

export const treatmentRuntimeDraftIdentity = ({
  session,
  sourceKind,
  sourceId,
  apiBaseUrl,
}) => ({
  schema_version: TREATMENT_RUNTIME_DRAFT_SCHEMA_VERSION,
  source_kind: text(sourceKind || session?.release?.source_kind),
  source_id: text(sourceId || session?.release?.source_id),
  runtime_api_base_url: normalizeRuntimeApiBaseUrl(
    apiBaseUrl || session?.runtime_api_base_url,
  ),
  session_token: text(session?.session_token),
  release_id: text(session?.release?.id),
  release_version: Number(session?.release?.version || 0),
  release_checksum: text(session?.release?.checksum),
  patient_bound: session?.patient_bound === true,
  patient_ref: text(session?.patient_ref),
});

export const isTreatmentRuntimeDraftIdentityComplete = (identity) => Boolean(
  identity
  && identity.schema_version === TREATMENT_RUNTIME_DRAFT_SCHEMA_VERSION
  && identity.source_kind
  && identity.source_id
  && identity.runtime_api_base_url
  && identity.session_token
  && identity.release_id
  && identity.release_version > 0
  && identity.release_checksum
);

export const sameTreatmentRuntimeDraftIdentity = (left, right) => (
  isTreatmentRuntimeDraftIdentityComplete(left)
  && isTreatmentRuntimeDraftIdentityComplete(right)
  && left.source_kind === right.source_kind
  && left.source_id === right.source_id
  && left.runtime_api_base_url === right.runtime_api_base_url
  && left.session_token === right.session_token
  && left.release_id === right.release_id
  && left.release_version === right.release_version
  && left.release_checksum === right.release_checksum
  && ((left.patient_bound === right.patient_bound && left.patient_ref === right.patient_ref) || (!left.patient_bound && right.patient_bound))
);

const validInlineConsentDraft = (value) => (
  value
  && typeof value === "object"
  && !Array.isArray(value)
  && typeof value.agreed === "boolean"
  && typeof value.consent_text === "string"
);

export const restoreTreatmentRuntimeDraft = ({
  draft,
  expectedIdentity,
  questionnaire,
  authoritativeResponses,
}) => {
  if (
    !draft
    || !sameTreatmentRuntimeDraftIdentity(draft.runtime_identity, expectedIdentity)
    || !draft.answers
    || typeof draft.answers !== "object"
    || Array.isArray(draft.answers)
  ) return null;

  const questionsById = new Map(
    (questionnaire?.questions || []).map((question) => [String(question.id), question]),
  );
  for (const [questionId, value] of Object.entries(draft.answers)) {
    const question = questionsById.get(String(questionId));
    if (
      isInlineProgramConsentQuestion(question)
      && !validInlineConsentDraft(value)
    ) return null;
  }

  return {
    ...draft,
    answers: {
      ...draft.answers,
      ...(authoritativeResponses || {}),
    },
  };
};
